import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * ActionSheet — bottom sheet mobile do DS (mockup `.asheet`).
 *
 * Substitui o dropdown de ações (•••) no mobile: sheet colado embaixo, grab handle,
 * itens 15px, separadores e cor `danger`. Tema claro fixo (padrão de overlays).
 * Fecha por scrim ou Escape.
 *
 * Uso: renderizar condicionalmente quando `useIsMobile()` for true, no lugar do
 * dropdown desktop. Cada menu passa seus próprios itens (paridade de ações).
 */

export interface ActionSheetItem {
  key: string;
  label: string;
  icon?: IconName;
  /** cor do texto/ícone (default #334155); use para ações verdes/vermelhas */
  color?: string;
  /** separador acima deste item */
  separatorBefore?: boolean;
  /** desabilita o item (ex.: mutation pendente) — paridade com o dropdown desktop */
  disabled?: boolean;
  /** testid do item */
  testId?: string;
}

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** título opcional no topo do sheet */
  title?: string;
  items: ActionSheetItem[];
  onSelect: (key: string) => void;
  /** testid do container */
  testId?: string;
}

export const ActionSheet: React.FC<ActionSheetProps> = ({
  isOpen,
  onClose,
  title,
  items,
  onSelect,
  testId,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 z-[530]"
        style={{ backgroundColor: 'rgba(15,23,42,.42)' }}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid={testId ? `${testId}-scrim` : undefined}
      />
      {/* Sheet */}
      <div
        className="fixed left-0 right-0 bottom-0 z-[531] pb-[max(8px,env(safe-area-inset-bottom))]"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '18px 18px 0 0',
          boxShadow: '0 -8px 32px rgba(0,0,0,.16)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="w-9 h-1 rounded-full mx-auto my-2.5" style={{ backgroundColor: '#e2e8f0' }} />
        {title && (
          <div className="px-4 pb-2 text-[13px] font-semibold" style={{ color: '#94a3b8' }}>
            {title}
          </div>
        )}
        <div className="px-2 pb-2">
          {items.map((it) => (
            <React.Fragment key={it.key}>
              {it.separatorBefore && (
                <div className="my-1 mx-2 border-t" style={{ borderColor: '#eef1f5' }} />
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onSelect(it.key); }}
                className="flex items-center gap-3.5 w-full text-left rounded-xl disabled:opacity-50"
                style={{ padding: '15px 14px', fontSize: 15, color: it.color || '#334155' }}
                disabled={it.disabled}
                data-testid={it.testId}
                onTouchStart={undefined}
              >
                {it.icon && (
                  <Icon name={it.icon} className="w-5 h-5 flex-shrink-0" style={{ color: it.color || '#64748b' }} />
                )}
                <span className="flex-1 min-w-0">{it.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
};

ActionSheet.displayName = 'ActionSheet';
